import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/Warning';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  endUserTransferExecute,
  endUserTransferLookup,
  warehouseTransferExecute,
  warehouseTransferLookup
} from 'src/features/deviceSlice';
import { getListAgencies } from 'src/features/agencySlice';
import { showGlobalToast } from 'src/features/uiSlice';
import axiosInterceptor from 'src/app/axiosInterceptor';
import { messageToastType_const } from 'src/app/constant/config';
import { parseSerialsInput } from 'src/app/views/manage/salesOrders/SerialDialog';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    width: '100%',
    maxWidth: 640,
    borderRadius: 14,
    overflow: 'hidden'
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    background: '#d9042f',
    color: '#fff'
  },
  dialogHeaderTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#fff'
  },
  closeButton: {
    color: '#fff',
    padding: 4
  },
  dialogContent: {
    padding: '20px',
    backgroundColor: '#fafbfc'
  },
  fieldRow: {
    marginBottom: 16,
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      backgroundColor: '#fff'
    }
  },
  fieldLabel: {
    fontSize: 15,
    fontWeight: 700,
    color: '#555555',
    marginBottom: 6
  },
  requiredMark: {
    color: '#D9042F',
    marginLeft: 4
  },
  helperText: {
    fontSize: 12,
    color: '#8a8a8a',
    marginTop: 6
  },
  notFoundBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '10px 12px',
    borderRadius: 12,
    backgroundColor: '#fdecea',
    border: '1px solid #f8d0d0',
    marginBottom: 16
  },
  notFoundText: {
    fontSize: 14,
    color: '#8a4a4a',
    lineHeight: 1.5
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: '#8a8a8a',
    marginBottom: 8
  },
  tableWrapper: {
    maxHeight: 260,
    overflow: 'auto',
    borderRadius: 12,
    border: '1px solid #eef1f6',
    marginBottom: 16
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    '& th, & td': {
      padding: '8px 10px',
      fontSize: 14,
      textAlign: 'left',
      borderBottom: '1px solid #eef1f6'
    },
    '& th': {
      position: 'sticky',
      top: 0,
      zIndex: 1,
      background: '#f5f7fb',
      color: '#45485E',
      fontWeight: 700
    },
    '& tbody tr:last-child td': {
      borderBottom: 'none'
    },
    '& tbody tr:hover': {
      backgroundColor: '#fafbfc'
    }
  },
  targetBox: {
    padding: '12px 14px',
    borderRadius: 12,
    backgroundColor: '#e8f0fe',
    border: '1px solid #cfe0fb',
    marginBottom: 16,
    fontSize: 14,
    color: '#1f2430'
  },
  dialogActions: {
    padding: '14px 20px',
    backgroundColor: '#fff',
    borderTop: '1px solid #eef1f6',
    gap: 8
  },
  primaryActionButton: {
    minWidth: 120,
    height: 40,
    borderRadius: 10,
    fontWeight: 800,
    fontStyle: 'normal',
    textTransform: 'none',
    boxShadow: 'none',
    background: '#d9042f',
    color: '#fff',
    '&:hover': {
      background: '#b80327'
    },
    '&.Mui-disabled': {
      background: '#f2b9c4',
      color: '#fff'
    }
  },
  secondaryActionButton: {
    minWidth: 100,
    height: 40,
    borderRadius: 10,
    fontWeight: 800,
    fontStyle: 'normal',
    textTransform: 'none',
    background: 'none',
    border: '1px solid #D9042F',
    color: '#D9042F',
    '&:hover': {
      border: '1px solid #D9042F',
      background: '#fdecea'
    }
  }
}));



// API /agencies chỉ trả đại lý cấp 1 ở gốc, đại lý cấp 2 nằm lồng trong `children`.
// Làm phẳng thành 1 mảng để đưa hết cấp 1 + cấp 2 (+ mọi cấp sâu hơn nếu có) vào Autocomplete.
const flattenAgencies = (agencies, parentName = '') => {
  const result = [];
  (agencies || []).forEach(agency => {
    result.push({ ...agency, parentName });
    if (agency.children?.length) {
      result.push(...flattenAgencies(agency.children, agency.name));
    }
  });
  return result;
};

// mode: 'warehouse' | 'end_user'
const BatchTransferModal = ({ open, onClose, mode, defaultSerial, onSuccess }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [serialsText, setSerialsText] = useState('');
  const [destinationAgencyId, setDestinationAgencyId] = useState(null);
  const [phone, setPhone] = useState('');
  const [step, setStep] = useState('input'); // input | preview
  const [lookupResult, setLookupResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);

  const listAgencies = useSelector(state => state.agencySlice.listAgencies);
  const isAgency = useSelector(state => state.authSlice.isAgency);
  const myAgencyId = useSelector(state => state.authSlice.agencyId);
  const myAgencyName = useSelector(state => state.authSlice.dataLogin?.agency?.name);
  const flatAgencies = useMemo(() => flattenAgencies(listAgencies), [listAgencies]);

  // Đại lý chỉ được chuyển trong phạm vi: kho của mình + đại lý con trực tiếp (cấp 2) đang active
  const [childAgencies, setChildAgencies] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const agencyScopeOptions = useMemo(
    () =>
      myAgencyId
        ? [
            { id: myAgencyId, name: `Kho của tôi${myAgencyName ? ` — ${myAgencyName}` : ''}` },
            ...childAgencies
          ]
        : childAgencies,
    [myAgencyId, myAgencyName, childAgencies]
  );
  const destinationOptions = isAgency ? agencyScopeOptions : flatAgencies;

  const isWarehouse = mode === 'warehouse';
  const isPhoneMissing = !isWarehouse && !phone.trim();
  const isSerialsMissing = parseSerialsInput(serialsText).length === 0;

  useEffect(() => {
    if (!open) return;
    setSerialsText(defaultSerial ? String(defaultSerial) : '');
    setDestinationAgencyId(null);
    setPhone('');
    setStep('input');
    setLookupResult(null);
    setLoading(false);
    setExecuting(false);

    if (isWarehouse && isAgency && myAgencyId) {
      let active = true;
      setLoadingChildren(true);
      axiosInterceptor
        .get('/agencies', { params: { parent_id: myAgencyId, page_size: 1000 } })
        .then(res => {
          if (!active) return;
          const agencies = res.data?.payload?.agencies || [];
          setChildAgencies(
            agencies.filter(a => a.status === 'active' && a.id !== myAgencyId)
          );
        })
        .catch(() => {
          if (active) setChildAgencies([]);
        })
        .finally(() => {
          if (active) setLoadingChildren(false);
        });
      return () => {
        active = false;
      };
    }

    if (isWarehouse && !isAgency && (!listAgencies || listAgencies.length === 0)) {
      dispatch(getListAgencies({ page_size: 1000 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, defaultSerial]);

  const handleClose = () => {
    if (loading || executing) return;
    onClose();
  };

  const handleLookup = async () => {
    const serials = parseSerialsInput(serialsText);
    if (serials.length === 0) {
      dispatch(
        showGlobalToast({ message: 'Vui lòng nhập ít nhất 1 serial', type: messageToastType_const.error })
      );
      return;
    }
    if (!isWarehouse && !phone.trim()) {
      dispatch(
        showGlobalToast({ message: 'Vui lòng nhập số điện thoại tài khoản người dùng cuối', type: messageToastType_const.error })
      );
      return;
    }

    setLoading(true);
    try {
      const action = isWarehouse
        ? await dispatch(warehouseTransferLookup({ serials })).unwrap()
        : await dispatch(endUserTransferLookup({ serials, phone: phone.trim() })).unwrap();

      setLookupResult(action?.payload || null);
      setStep('preview');
    } catch (err) {
      dispatch(
        showGlobalToast({
          message: err?.message || (isWarehouse ? 'Không tra cứu được danh sách thiết bị' : 'Không tìm thấy tài khoản end-user theo số điện thoại này'),
          type: messageToastType_const.error
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    const serials = parseSerialsInput(serialsText);
    if (isWarehouse && !destinationAgencyId) {
      dispatch(
        showGlobalToast({ message: 'Vui lòng chọn đại lý đích', type: messageToastType_const.error })
      );
      return;
    }

    setExecuting(true);
    try {
      const action = isWarehouse
        ? await dispatch(
            warehouseTransferExecute({ serials, destination_agency_id: destinationAgencyId })
          ).unwrap()
        : await dispatch(
            endUserTransferExecute({ serials, phone: phone.trim() })
          ).unwrap();

      const payload = action?.payload || {};
      const movedCount = payload.moved?.length || 0;
      const skippedCount = payload.skipped?.length || 0;
      dispatch(
        showGlobalToast({
          message: `Chuyển thành công: ${movedCount} thiết bị, bỏ qua ${skippedCount}`,
          type: messageToastType_const.success
        })
      );
      if (onSuccess) onSuccess(payload);
      onClose();
    } catch (err) {
      // Lỗi "thiếu serial" (all-or-nothing) -> đưa user về bước lookup để đối chiếu lại
      setStep('input');
      setLookupResult(null);
    } finally {
      setExecuting(false);
    }
  };

  const notFound = lookupResult?.not_found || [];
  const devices = lookupResult?.devices || [];
  const target = lookupResult?.target;

  return (
    <Dialog open={open} onClose={handleClose} classes={{ paper: classes.dialogPaper }}>
      <Box className={classes.dialogHeader}>
        <Typography className={classes.dialogHeaderTitle}>
          {isWarehouse ? 'Chuyển kho thiết bị (theo lô)' : 'Đổi khách hàng (theo lô)'}
        </Typography>
        <IconButton
          size="small"
          className={classes.closeButton}
          onClick={handleClose}
          disabled={loading || executing}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent className={classes.dialogContent}>
        {step === 'input' && (
          <>
            <Box className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>
                Danh sách serial (mỗi dòng 1 serial)
                <span className={classes.requiredMark}>*</span>
              </Typography>
              <TextField
                fullWidth
                required
                multiline
                minRows={5}
                variant="outlined"
                placeholder={'VD: S001\nS002\nS003'}
                value={serialsText}
                onChange={e => setSerialsText(e.target.value)}
              />
              <Typography className={classes.helperText}>
                Tối đa 500 serial
              </Typography>
            </Box>

            {!isWarehouse && (
              <Box className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>
                  Số điện thoại người dùng cuối
                  <span className={classes.requiredMark}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  placeholder="Nhập số điện thoại người dùng cuối"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                />
              </Box>
            )}
          </>
        )}

        {step === 'preview' && (
          <>
            {!isWarehouse && target && (
              <Box className={classes.targetBox}>
                <strong>Tài khoản người dùng cuối:</strong> {target.full_name} ({target.phone})
              </Box>
            )}

            {notFound.length > 0 && (
              <Box className={classes.notFoundBox}>
                <WarningIcon fontSize="small" style={{ color: '#d9042f', flexShrink: 0, marginTop: 1 }} />
                <Typography className={classes.notFoundText}>
                  Các serial sau <strong>{isAgency ? 'không thuộc kho của bạn hoặc không tồn tại' : 'không tồn tại'}</strong>: {notFound.join(', ')}. Nếu thực hiện,
                  toàn bộ lô sẽ bị hủy (không thiết bị nào bị đổi).
                </Typography>
              </Box>
            )}

            <Typography className={classes.sectionLabel}>
              Danh sách thiết bị ({devices.length})
            </Typography>
            <Box className={classes.tableWrapper}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th>Serial</th>
                    <th>Trạng thái</th>
                    {isWarehouse ? (
                      <>
                        <th>Đại lý hiện tại</th>
                        <th>Biển số</th>
                      </>
                    ) : (
                      <th>Người dùng hiện tại</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {devices.map(device => (
                    <tr key={device.id}>
                      <td>{device.serial}</td>
                      <td>{device.status}</td>
                      {isWarehouse ? (
                        <>
                          <td>{device.agency_name || '--'}</td>
                          <td>{device.license_plate || '--'}</td>
                        </>
                      ) : (
                        <td>
                          {device.current_end_user_name
                            ? `${device.current_end_user_name} (${device.current_end_user_phone || '--'})`
                            : '--'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>

            {isWarehouse && (
              <Box className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Đại lý đích</Typography>
                <Autocomplete
                  options={destinationOptions}
                  loading={loadingChildren}
                  noOptionsText={isAgency ? 'Không có đại lý cấp 2 đang hoạt động' : 'Không có dữ liệu'}
                  getOptionLabel={option =>
                    option?.parentName ? `${option.name}` : option?.name || ''
                  }
                  onChange={(e, value) => setDestinationAgencyId(value?.id || null)}
                  renderInput={params => (
                    <TextField {...params} variant="outlined" placeholder="Chọn đại lý đích" />
                  )}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        {step === 'input' ? (
          <>
            <Button className={classes.secondaryActionButton} onClick={handleClose} disabled={loading}>
              Hủy
            </Button>
            <Button
              className={classes.primaryActionButton}
              onClick={handleLookup}
              disabled={loading || isSerialsMissing || isPhoneMissing}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Xem trước
            </Button>
          </>
        ) : (
          <>
            <Button className={classes.secondaryActionButton} onClick={() => setStep('input')} disabled={executing}>
              Quay lại
            </Button>
            <Button
              className={classes.primaryActionButton}
              onClick={handleExecute}
              disabled={executing || devices.length === 0}
              startIcon={executing ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Xác nhận
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BatchTransferModal;
